'use client';

/**
 * RecapShareButton — compact share affordance for the chain Overview recap hero.
 *
 * Migration Ledger §4: the daily-recap / overall share card must keep a Share
 * affordance. This is a small gold-outline "Share" button that opens a tiny
 * popover with "Share on X" (twitter intent) + "Copy link" (clipboard).
 *
 * It is a SIBLING of the recap <Link> (never nested inside the anchor) so the
 * crawlable headline/body remains a plain server-rendered link. Reuses the GA4
 * `trackShare` helper (contentType: 'article', same shape as ShareButtons).
 */

import { useEffect, useRef, useState } from 'react';
import { Share2, Check } from 'lucide-react';
import { XIcon } from '@/components/icons/SocialIcons';
import { trackShare } from '@/lib/analytics';

export default function RecapShareButton({
  title,
  url,
}: {
  /** Recap headline — used in the tweet text + GA label. */
  title: string;
  /** Absolute recap URL (${SITE_URL}${recap.href}). */
  url: string;
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const shareOnX = () => {
    const text = `Check out this recap: ${title}`;
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      '_blank',
      'noopener,noreferrer',
    );
    trackShare({ platform: 'twitter', contentType: 'article', contentTitle: title, contentUrl: url });
    setOpen(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      trackShare({ platform: 'copy_link', contentType: 'article', contentTitle: title, contentUrl: url });
    } catch {
      /* clipboard unavailable — no-op */
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Share this recap"
        className="inline-flex items-center gap-1.5 rounded border-[0.5px] border-scout-gold/50 bg-surface/70 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.1em] text-scout-gold backdrop-blur transition-colors hover:border-scout-gold hover:bg-scout-gold/10"
      >
        <Share2 size={14} />
        Share
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1.5 w-40 overflow-hidden rounded border-[0.5px] border-divider bg-surface shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            onClick={shareOnX}
            className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left font-display text-[13px] text-parchment transition-colors hover:bg-surface2"
          >
            <XIcon className="h-3.5 w-3.5 flex-shrink-0" />
            Share on X
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={copyLink}
            className="flex w-full items-center gap-2.5 border-t border-divider px-3 py-2.5 text-left font-display text-[13px] text-parchment transition-colors hover:bg-surface2"
          >
            {copied ? (
              <Check size={14} className="flex-shrink-0 text-signal-green" />
            ) : (
              <Share2 size={14} className="flex-shrink-0" />
            )}
            {copied ? 'Copied' : 'Copy link'}
          </button>
        </div>
      )}
    </div>
  );
}
